// F8 / FR-RD15: polls the desktop shell's `meeting_windows` command (web-layer polling — the
// Rust side stays a stateless query) and surfaces the first meeting window we have not yet
// offered. It only ever *offers*: nothing here starts a recording.
import { useCallback, useEffect, useRef, useState } from "react";
import { isDesktop, isMobileShell, notificationPermissionGranted, sendOsNotification } from "../desktop";
import {
  MEETING_POLL_MS,
  appLabel,
  getOfferSetting,
  listMeetingWindows,
  newlyDetected,
  type MeetingWindow,
} from "./meetingCapture";

export function useMeetingDetector(enabled = true): { candidate: MeetingWindow | null; dismiss: () => void } {
  const [candidate, setCandidate] = useState<MeetingWindow | null>(null);
  const offered = useRef(new Set<string>());
  const dismissed = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled || !isDesktop() || isMobileShell()) return;
    let cancelled = false;
    async function poll() {
      if (!getOfferSetting()) {
        setCandidate(null);
        return;
      }
      let windows: MeetingWindow[];
      try {
        windows = await listMeetingWindows();
      } catch {
        return; // detection is best effort; never disturbs the app
      }
      if (cancelled) return;
      const fresh = newlyDetected(windows, offered.current);
      for (const w of fresh) offered.current.add(w.source_id);
      // A window that has closed may reappear under the same id later: offer it again.
      const live = new Set(windows.map((w) => w.source_id));
      for (const id of [...offered.current]) if (!live.has(id)) offered.current.delete(id);
      for (const id of [...dismissed.current]) if (!live.has(id)) dismissed.current.delete(id);
      const open = windows.filter((w) => !dismissed.current.has(w.source_id));
      setCandidate((prev) => open.find((w) => w.source_id === prev?.source_id) ?? open[0] ?? null);
      if (fresh.length > 0) {
        // Non-intrusive OS notification; permission is never prompted for from here.
        try {
          if (await notificationPermissionGranted()) {
            await sendOsNotification(`${appLabel(fresh[0].app)} meeting detected`, "Record this meeting? Open Grackle to start.");
          }
        } catch {
          /* notification is a nicety */
        }
      }
    }
    void poll();
    const id = setInterval(() => void poll(), MEETING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled]);

  const dismiss = useCallback(() => {
    setCandidate((c) => {
      if (c) dismissed.current.add(c.source_id);
      return null;
    });
  }, []);

  return { candidate, dismiss };
}
